import { markRaw } from 'vue'

import type { I18n } from '@backmarket/nuxt-module-i18n/types'
import { IconBattery } from '@ds/icons/IconBattery'
import { IconDesign } from '@ds/icons/IconDesign'
import { IconPackage } from '@ds/icons/IconPackage'
import { IconPerformance } from '@ds/icons/IconPerformance'
import { IconPowerAdapterApple } from '@ds/icons/IconPowerAdapterApple'
import { IconSmartphoneCamera } from '@ds/icons/IconSmartphoneCamera'
import { IconTruckExpress } from '@ds/icons/IconTruckExpress'

import type {
  GetReviewCollectionResponse,
  Question,
} from '~/api/reviews/types/GetReviewCollectionResponse'

import translations from '../ReviewCollection.translations'
import type { ReviewFormQuestion } from '../models/review-form'

function getQuestionTextAndIcon({
  question,
  i18n,
  model,
}: {
  question: Question
  i18n: I18n
  model: string
}) {
  switch (question.identifier) {
    case 'review_main_rate':
      return {
        title: i18n(translations.mainRateTitle, { model }),
        subtitle: i18n(translations.mainRateSubtitle),
      }

    case 'review_overall_performance':
      return {
        title: i18n(translations.overallPerformanceTitle),
        icon: markRaw(IconPerformance),
      }

    case 'review_aesthetic_appearance':
      return {
        title: i18n(translations.aestheticAppearanceTitle),
        icon: markRaw(IconDesign),
      }

    case 'review_battery':
      return {
        title: i18n(translations.batteryTitle),
        icon: markRaw(IconBattery),
      }

    case 'review_camera':
      return {
        title: i18n(translations.cameraTitle),
        icon: markRaw(IconSmartphoneCamera),
      }

    case 'review_accessories':
      return {
        title: i18n(translations.accessoriesTitle),
        icon: markRaw(IconPowerAdapterApple),
      }

    case 'review_shipping':
      return {
        title: i18n(translations.shippingTitle),
        icon: markRaw(IconTruckExpress),
      }

    case 'review_packaging_cleanliness':
      return {
        title: i18n(translations.packagingCleanlinessTitle),
        icon: markRaw(IconPackage),
      }

    default:
      return {
        title: '',
        subtitle: '',
      }
  }
}

function addDataToQuestions({
  questions,
  i18n,
  productModel,
}: {
  questions: Question[]
  i18n: I18n
  productModel: string
}): ReviewFormQuestion[] {
  return questions.map((question) => {
    const { subtitle, title, icon } = getQuestionTextAndIcon({
      question,
      i18n,
      model: productModel,
    })

    return {
      identifier: question.identifier,
      type: question.type,
      title,
      subtitle,
      icon,
      required: question.required,
    } satisfies ReviewFormQuestion
  })
}

export function buildQuestions({
  stepIndex,
  apiData,
  i18n,
  productModel,
}: {
  stepIndex: number
  apiData: GetReviewCollectionResponse
  i18n: I18n
  productModel: string
}) {
  const currentStep = apiData.steps[stepIndex]

  const { questions: apiQuestions } = currentStep

  return addDataToQuestions({
    questions: apiQuestions,
    i18n,
    productModel,
  })
}
